import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import MidtownHero from '../components/midtown/MidtownHero';
import MidtownLunchReset from '../components/midtown/MidtownLunchReset';
import MidtownGettingHere from '../components/midtown/MidtownGettingHere';
import MidtownNeighborhoods from '../components/midtown/MidtownNeighborhoods';
import MidtownServices from '../components/midtown/MidtownServices';
import MidtownFaq from '../components/midtown/MidtownFaq';
import { PAGE_URL, MIDTOWN_FAQ, LUNCH_RESET } from '../components/midtown/midtownData';

const TITLE = 'Massage Therapy in Midtown Sacramento | Certified Massage Therapist – Ayumi Suzuki';
const DESCRIPTION = 'CAMTC-certified massage therapist in Midtown Sacramento. Swedish, deep tissue & therapeutic massage at 2528 I Street, 95816. Parking, map, hours & pricing from $55.';

const schema = [
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ayumisuzuki.com' },
      { '@type': 'ListItem', position: 2, name: 'Massage in Midtown Sacramento', item: PAGE_URL },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Midtown Lunch Reset — 30-Minute Massage',
    serviceType: 'Massage Therapy',
    description: 'A focused 30-minute lunch-break massage for neck, shoulders and upper back, for state workers and Sutter staff in Midtown Sacramento.',
    url: `${PAGE_URL}#lunch-reset`,
    provider: { '@id': 'https://ayumisuzuki.com/#business' },
    areaServed: { '@type': 'Place', name: 'Midtown, Sacramento, CA' },
    hoursAvailable: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'], opens: '11:00', closes: '14:00' },
    offers: { '@type': 'Offer', price: String(LUNCH_RESET.price), priceCurrency: 'USD', url: 'https://ayumisuzuki.com/book' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: MIDTOWN_FAQ.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  },
];

const setMeta = (title, description) => {
  document.title = title;
  document.querySelector('meta[name="description"]')?.setAttribute('content', description);
};

const MidtownStudioPage = () => {
  const navigate = useNavigate();
  const goToHomeSection = (id) => {
    navigate('/');
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 400);
  };

  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute('content');
    setMeta(TITLE, DESCRIPTION);
    return () => setMeta(prevTitle, prevDesc);
  }, []);

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="midtown-studio-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-700 hover:text-teal-700 transition-colors duration-300 group" data-testid="midtown-back-home-link">
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
          <span className="font-serif tracking-wide">AYUMI SUZUKI</span>
        </Link>
        <Link to="/book" className="bg-teal-700 text-white text-sm px-5 py-2.5 rounded-full hover:bg-gray-900 transition-colors duration-300" data-testid="midtown-header-book-btn">
          Book now
        </Link>
      </header>
      <main>
        <MidtownHero />
        <MidtownLunchReset />
        <MidtownGettingHere />
        <MidtownNeighborhoods />
        <MidtownServices />
        <MidtownFaq />
      </main>
      <Footer scrollToSection={goToHomeSection} />
    </div>
  );
};

export default MidtownStudioPage;
